import { environment } from 'src/environments/environment';

/**
 * okta org a user can be launched from
 * every realm has its own okta organization and its own b2c sign in policy
 */
export type OktaRealm = 'trinity' | 'strata' | 'srta';

/**
 * everything that differs between the okta realms
 */
export interface OktaRealmConfigInterface {
  /** realm key persisted in localStorage under 'oktaRealm' */
  readonly realm: OktaRealm;
  /** percent encoded okta org url without a trailing slash, as it appears in the environment and in the launch url */
  readonly encodedOrgUrl: string;
  /** azure ad b2c sign in user flow (policy) name of this realm */
  readonly signInPolicy: string;
}

/**
 * realm assumed when nothing usable is stored
 * sessions created before the extra realms existed have no 'oktaRealm' entry at all
 */
export const DEFAULT_OKTA_REALM: OktaRealm = 'trinity';

/**
 * configuration of the default realm, also the first entry of {@link OKTA_REALMS}
 */
const DEFAULT_OKTA_REALM_CONFIG: OktaRealmConfigInterface = {
  realm: DEFAULT_OKTA_REALM,
  encodedOrgUrl: environment.oktaRedirectUri,
  signInPolicy: environment.signInOkta,
};

/**
 * single source of truth for the supported okta realms
 * add a realm here and both the org url and the sign in policy resolution pick it up
 */
export const OKTA_REALMS: readonly OktaRealmConfigInterface[] = [
  DEFAULT_OKTA_REALM_CONFIG,
  {
    realm: 'strata',
    encodedOrgUrl: environment.strataRedirectUri,
    signInPolicy: environment.signInStrata,
  },
  {
    realm: 'srta',
    encodedOrgUrl: environment.srtaRedirectUri,
    signInPolicy: environment.signInSrta,
  },
];

/**
 * configuration of the given realm
 * @param realm - stored realm key, possibly missing or unknown
 * @returns the matching configuration, or the {@link DEFAULT_OKTA_REALM_CONFIG} one when the realm is missing or unrecognized
 */
export function findOktaRealmConfig(realm: string | null): OktaRealmConfigInterface {
  const config = OKTA_REALMS.find((oktaRealmConfig: OktaRealmConfigInterface) => oktaRealmConfig.realm === realm);
  return config ?? DEFAULT_OKTA_REALM_CONFIG;
}

/**
 * realm a launch url points at, matched on the percent encoded okta org url it carries
 * pure - it looks at the passed string only, never at the persisted realm
 * first match in {@link OKTA_REALMS} wins - a launch url is assumed to carry at most one known okta org marker
 * (that assumption holds because no encoded org url is a substring of another)
 * @param search - url or query string to inspect, e.g. window.location.search
 * @returns the matching realm, or null when the string carries no known okta org url
 */
export function resolveOktaRealmFromUrl(search: string): OktaRealm | null {
  if (!search) return null;

  const config = OKTA_REALMS.find(
    (oktaRealmConfig: OktaRealmConfigInterface) => !!oktaRealmConfig.encodedOrgUrl && search.includes(oktaRealmConfig.encodedOrgUrl),
  );
  return config ? config.realm : null;
}
